'use client';

import { useRef, useState } from 'react';

interface Segment {
  speaker: string;
  start: number;
  end: number;
  text: string;
}
interface Result {
  segments: Segment[];
  speakerCount: number;
  namesFound: number;
  formats: { txt: string; srt: string; vtt: string };
}

type Phase = 'form' | 'uploading' | 'processing' | 'done' | 'error';

const inputClass =
  'w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 font-mk-body text-mk-ink placeholder:text-mk-muted/50 outline-none transition-colors focus:border-mk-pink/60';

function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// Stable colour per speaker label so the eye can follow a conversation.
const SPEAKER_COLORS = ['text-mk-cyan', 'text-mk-teal', 'text-mk-pink-soft', 'text-mk-ink', 'text-amber-300'];

export default function TranscribeTool({ enabled = true }: { enabled?: boolean }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [copied, setCopied] = useState(false);
  const speakerColor = useRef<Record<string, string>>({});

  function colorFor(speaker: string): string {
    if (!speakerColor.current[speaker]) {
      const n = Object.keys(speakerColor.current).length;
      speakerColor.current[speaker] = SPEAKER_COLORS[n % SPEAKER_COLORS.length];
    }
    return speakerColor.current[speaker];
  }

  function fail(msg: string) {
    setError(msg);
    setPhase('error');
  }

  async function pollStatus(jobId: string) {
    try {
      const res = await fetch(`/api/public/transcribe/status?jobId=${encodeURIComponent(jobId)}`);
      const data = await res.json();
      if (data.status === 'processing') {
        setTimeout(() => pollStatus(jobId), 4000);
        return;
      }
      if (data.status === 'completed') {
        setResult(data);
        setPhase('done');
        return;
      }
      fail(data.detail || 'Transcription failed.');
    } catch {
      setTimeout(() => pollStatus(jobId), 5000);
    }
  }

  async function run(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return fail('Choose a video or audio file first.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return fail('Enter a valid email.');
    setError(null);
    setPhase('uploading');

    // Capture the lead (best-effort, don't block on it).
    fetch('/api/public/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, tool: 'transcribe' }),
    }).catch(() => {});

    try {
      // 1) presigned upload
      const presignRes = await fetch('/api/public/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          file_size: file.size,
          content_type: file.type || 'video/mp4',
          tool: 'transcribe',
        }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok) return fail(presign.detail || 'Upload could not start.');

      // 2) upload straight to storage
      const fd = new FormData();
      Object.entries(presign.upload.fields as Record<string, string>).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', file);
      const uploadRes = await fetch(presign.upload.url, { method: 'POST', body: fd });
      if (!uploadRes.ok) return fail('Upload failed — please try a smaller file or try again.');

      // 3) start transcription
      setPhase('processing');
      const startRes = await fetch('/api/public/transcribe/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: presign.key }),
      });
      const start = await startRes.json();
      if (!startRes.ok) return fail(start.detail || 'Could not start transcription.');

      // 4) poll
      pollStatus(start.jobId);
    } catch {
      fail('Something went wrong — please try again.');
    }
  }

  function download(kind: 'txt' | 'srt' | 'vtt') {
    if (!result) return;
    const blob = new Blob([result.formats[kind]], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.formats.txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* selection still possible */
    }
  }

  function reset() {
    setPhase('form');
    setFile(null);
    setResult(null);
    setError(null);
    speakerColor.current = {};
  }

  const busy = phase === 'uploading' || phase === 'processing';

  if (!enabled) {
    return (
      <div className="not-prose rounded-3xl border border-mk-cyan/30 bg-mk-cyan/10 px-6 py-10 text-center">
        <p className="font-mk-body text-lg text-mk-ink">This tool is coming soon.</p>
        <p className="mt-2 font-mk-body text-sm text-mk-muted">
          We&apos;re putting the finishing touches on it — check back shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="not-prose">
      {(phase === 'form' || phase === 'error') && (
        <form onSubmit={run} className="space-y-4 rounded-3xl border border-white/10 bg-white/[0.04] p-6">
          <label className="block">
            <span className="mb-1.5 block font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-muted">
              Video or audio file
            </span>
            <input
              type="file"
              accept="video/*,audio/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-mk-muted file:mr-4 file:rounded-lg file:border-0 file:bg-mk-pink file:px-4 file:py-2 file:font-mk-label file:text-xs file:font-bold file:uppercase file:tracking-[0.14em] file:text-mk-on-pink hover:file:opacity-90"
            />
            {file && <span className="mt-1 block text-xs text-mk-muted">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <input className={inputClass} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
            <input className={inputClass} type="email" placeholder="Email (to get your transcript)" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          {error && (
            <p className="rounded-xl border border-mk-pink/30 bg-mk-pink/10 px-4 py-3 font-mk-body text-sm text-mk-pink-soft">{error}</p>
          )}
          <button
            type="submit"
            className="w-full rounded-xl bg-mk-pink px-6 py-3 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90"
          >
            Transcribe
          </button>
          <p className="text-center font-mk-body text-xs text-mk-muted">
            Free · any video or audio · up to 500 MB · speakers + timestamps included.
          </p>
        </form>
      )}

      {busy && (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.04] p-12 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-mk-pink/30 border-t-mk-pink" />
          <p className="font-mk-body text-mk-ink">
            {phase === 'uploading' ? 'Uploading your file…' : 'Transcribing — this can take a minute or two for longer videos.'}
          </p>
          <p className="font-mk-body text-xs text-mk-muted">Keep this tab open. We&apos;re adding speaker labels and timestamps.</p>
        </div>
      )}

      {phase === 'done' && result && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4">
            <span className="font-mk-body text-sm text-mk-muted">
              {result.segments.length} segments · {result.speakerCount} speaker{result.speakerCount === 1 ? '' : 's'}
              {result.namesFound > 0 ? ` · ${result.namesFound} named` : ''}
            </span>
            <div className="flex flex-wrap gap-2">
              <button onClick={copy} className="rounded-lg border border-white/10 px-3 py-1.5 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-ink transition-colors hover:border-mk-pink/50">
                {copied ? 'Copied!' : 'Copy'}
              </button>
              {(['txt', 'srt', 'vtt'] as const).map((k) => (
                <button key={k} onClick={() => download(k)} className="rounded-lg bg-mk-pink px-3 py-1.5 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90">
                  .{k}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[32rem] space-y-4 overflow-auto rounded-3xl border border-white/10 bg-mk-s-lowest p-6">
            {result.segments.map((s, i) => (
              <div key={i}>
                <div className="mb-1 flex items-baseline gap-2">
                  <span className={`font-mk-label text-xs font-bold uppercase tracking-[0.12em] ${colorFor(s.speaker)}`}>{s.speaker}</span>
                  <span className="font-mono-ibm text-[11px] text-mk-muted">{clock(s.start)}</span>
                </div>
                <p className="font-mk-body text-mk-ink">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={reset} className="rounded-xl border border-white/10 px-4 py-2 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-ink transition-colors hover:border-mk-pink/50">
              Transcribe another
            </button>
            <a href="/register" className="font-mk-body text-sm text-mk-muted">
              Want this across your whole video library, searchable? <span className="text-mk-pink">Try Reclipt →</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
